/**
 * DDL (Data Definition Language) untuk SQLite cache schema.
 *
 * Sumber: docs/04-Database.md §6 (SQLite Offline Cache Schema) & §10.2 (SQLite Indexes).
 * Tepat 11 tabel cache + 4 indexes, sesuai dokumentasi v0.2.0.
 *
 * Catatan:
 * - Menggunakan CREATE TABLE IF NOT EXISTS agar idempoten (aman dijalankan ulang).
 * - Timestamp default menggunakan datetime('now') (UTC ISO 8601 di SQLite).
 * - Kolom JSON (settings, structure, metadata, payload) disimpan sebagai TEXT;
 *   aplikasi bertanggung jawab parse/stringify.
 */

// ---------------------------------------------------------------------------
// CREATE TABLE statements (11 tabel)
// ---------------------------------------------------------------------------

export const CREATE_SYNC_META = /* sql */ `
CREATE TABLE IF NOT EXISTS sync_meta (
    key             TEXT PRIMARY KEY,
    value           TEXT NOT NULL,
    updated_at      TEXT NOT NULL DEFAULT (datetime('now'))
);
`;

export const CREATE_WORKSPACE_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS workspace_cache (
    id              TEXT PRIMARY KEY,
    tenant_id       TEXT NOT NULL,
    owner_id        TEXT NOT NULL,
    name            TEXT NOT NULL,
    description     TEXT,
    settings        TEXT NOT NULL DEFAULT '{}',
    sync_status     TEXT NOT NULL DEFAULT 'synced',
    last_synced_at  TEXT,
    updated_at      TEXT NOT NULL DEFAULT (datetime('now'))
);
`;

export const CREATE_CATEGORY_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS category_cache (
    id              TEXT PRIMARY KEY,
    workspace_id    TEXT NOT NULL,
    tenant_id       TEXT NOT NULL,
    name            TEXT NOT NULL,
    color           TEXT DEFAULT '#8B6F47',
    icon            TEXT,
    sort_order      INTEGER NOT NULL DEFAULT 0,
    sync_status     TEXT NOT NULL DEFAULT 'synced',
    last_synced_at  TEXT,
    updated_at      TEXT NOT NULL DEFAULT (datetime('now'))
);
`;

export const CREATE_ACTIVITY_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS activity_cache (
    id                  TEXT PRIMARY KEY,
    tenant_id           TEXT NOT NULL,
    workspace_id        TEXT NOT NULL,
    category_id         TEXT,
    template_id         TEXT,
    title               TEXT NOT NULL,
    description         TEXT,
    activity_date       TEXT NOT NULL,
    start_time          TEXT,
    end_time            TEXT,
    duration_minutes    INTEGER,
    location            TEXT,
    status              TEXT NOT NULL DEFAULT 'draft',
    priority            TEXT NOT NULL DEFAULT 'normal',
    metadata            TEXT NOT NULL DEFAULT '{}',
    created_by          TEXT NOT NULL,
    updated_by          TEXT,
    sync_status         TEXT NOT NULL DEFAULT 'pending',
    last_synced_at      TEXT,
    created_at          TEXT NOT NULL DEFAULT (datetime('now')),
    updated_at          TEXT NOT NULL DEFAULT (datetime('now')),
    deleted_at          TEXT
);
`;

export const CREATE_TAG_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS tag_cache (
    id              TEXT PRIMARY KEY,
    workspace_id    TEXT NOT NULL,
    tenant_id       TEXT NOT NULL,
    name            TEXT NOT NULL,
    color           TEXT DEFAULT '#C9A227',
    sync_status     TEXT NOT NULL DEFAULT 'synced',
    last_synced_at  TEXT,
    UNIQUE(workspace_id, name)
);
`;

export const CREATE_ACTIVITY_TAG_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS activity_tag_cache (
    activity_id     TEXT NOT NULL,
    tag_id          TEXT NOT NULL,
    PRIMARY KEY (activity_id, tag_id)
);
`;

export const CREATE_TEMPLATE_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS template_cache (
    id                  TEXT PRIMARY KEY,
    workspace_id        TEXT NOT NULL,
    tenant_id           TEXT NOT NULL,
    category_id         TEXT,
    name                TEXT NOT NULL,
    description         TEXT,
    structure           TEXT NOT NULL DEFAULT '{}',
    default_duration    INTEGER,
    default_priority    TEXT DEFAULT 'normal',
    usage_count         INTEGER NOT NULL DEFAULT 0,
    created_by          TEXT NOT NULL,
    sync_status         TEXT NOT NULL DEFAULT 'synced',
    last_synced_at      TEXT,
    created_at          TEXT NOT NULL DEFAULT (datetime('now')),
    updated_at          TEXT NOT NULL DEFAULT (datetime('now')),
    deleted_at          TEXT
);
`;

export const CREATE_EVIDENCE_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS evidence_cache (
    id              TEXT PRIMARY KEY,
    tenant_id       TEXT NOT NULL,
    activity_id     TEXT NOT NULL,
    file_name       TEXT NOT NULL,
    mime_type       TEXT NOT NULL,
    file_size       INTEGER NOT NULL,
    local_path      TEXT,
    storage_path    TEXT,
    checksum        TEXT,
    thumbnail_path  TEXT,
    metadata        TEXT NOT NULL DEFAULT '{}',
    created_by      TEXT NOT NULL,
    sync_status     TEXT NOT NULL DEFAULT 'pending',
    last_synced_at  TEXT,
    created_at      TEXT NOT NULL DEFAULT (datetime('now')),
    updated_at      TEXT NOT NULL DEFAULT (datetime('now')),
    deleted_at      TEXT
);
`;

export const CREATE_SYNC_QUEUE_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS sync_queue_cache (
    id              TEXT PRIMARY KEY,
    workspace_id    TEXT NOT NULL,
    entity_type     TEXT NOT NULL,
    entity_id       TEXT NOT NULL,
    operation       TEXT NOT NULL,
    payload         TEXT NOT NULL,
    status          TEXT NOT NULL DEFAULT 'pending',
    retry_count     INTEGER NOT NULL DEFAULT 0,
    max_retries     INTEGER NOT NULL DEFAULT 5,
    last_error      TEXT,
    next_retry_at   TEXT,
    created_at      TEXT NOT NULL DEFAULT (datetime('now')),
    updated_at      TEXT NOT NULL DEFAULT (datetime('now'))
);
`;

export const CREATE_SETTINGS_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS settings_cache (
    id              TEXT PRIMARY KEY,
    workspace_id    TEXT NOT NULL,
    category        TEXT NOT NULL,
    key             TEXT NOT NULL,
    value           TEXT NOT NULL,
    updated_at      TEXT NOT NULL DEFAULT (datetime('now')),
    UNIQUE(workspace_id, category, key)
);
`;

export const CREATE_AI_MEMORY_CACHE = /* sql */ `
CREATE TABLE IF NOT EXISTS ai_memory_cache (
    id              TEXT PRIMARY KEY,
    workspace_id    TEXT NOT NULL,
    memory_type     TEXT NOT NULL,
    key             TEXT NOT NULL,
    value           TEXT NOT NULL,
    confidence      REAL DEFAULT 0.5,
    sync_status     TEXT NOT NULL DEFAULT 'synced',
    last_synced_at  TEXT,
    updated_at      TEXT NOT NULL DEFAULT (datetime('now')),
    UNIQUE(workspace_id, memory_type, key)
);
`;

// ---------------------------------------------------------------------------
// CREATE INDEX statements (4 indexes — docs/04-Database.md §10.2)
// ---------------------------------------------------------------------------

export const CREATE_INDEXES = /* sql */ `
CREATE INDEX IF NOT EXISTS idx_activity_cache_workspace_date
    ON activity_cache(workspace_id, activity_date DESC);

CREATE INDEX IF NOT EXISTS idx_activity_cache_sync_status
    ON activity_cache(sync_status) WHERE sync_status != 'synced';

CREATE INDEX IF NOT EXISTS idx_sync_queue_cache_status
    ON sync_queue_cache(status, next_retry_at);

CREATE INDEX IF NOT EXISTS idx_evidence_cache_activity
    ON evidence_cache(activity_id);
`;

// ---------------------------------------------------------------------------
// Combined schema V1 — semua CREATE statements dalam urutan dependency
// ---------------------------------------------------------------------------

/**
 * DDL lengkap untuk schema versi 1.
 * Urutan penting: tabel sebelum index (index butuh tabel ada).
 */
export const SCHEMA_V1: string = [
  CREATE_SYNC_META,
  CREATE_WORKSPACE_CACHE,
  CREATE_CATEGORY_CACHE,
  CREATE_ACTIVITY_CACHE,
  CREATE_TAG_CACHE,
  CREATE_ACTIVITY_TAG_CACHE,
  CREATE_TEMPLATE_CACHE,
  CREATE_EVIDENCE_CACHE,
  CREATE_SYNC_QUEUE_CACHE,
  CREATE_SETTINGS_CACHE,
  CREATE_AI_MEMORY_CACHE,
  CREATE_INDEXES,
].join('\n');

/** Daftar semua nama tabel cache (untuk verifikasi/debugging). */
export const CACHE_TABLE_NAMES = [
  'sync_meta',
  'workspace_cache',
  'category_cache',
  'activity_cache',
  'tag_cache',
  'activity_tag_cache',
  'template_cache',
  'evidence_cache',
  'sync_queue_cache',
  'settings_cache',
  'ai_memory_cache',
] as const;
